import {
  withLoadedProcessWorkflow,
  type IManagedBindingSdStateLike,
  type IManagedBindingWorkflowLike
} from "./managed-screen-binding-sources";
import { findManagedTaskScreenLink, type IManagedTaskScreenLink } from "./managed-task-screen";

/**
 * KOA-18136 — make a value the inventory OFFERS actually writable.
 *
 * Reading the inventory of a managed task screen fetches the workflow when
 * designer state does not carry its parameters. Writing a binding to one of
 * those parameters resolved the same list from designer state alone, because the
 * write path is synchronous down through the picker engine and cannot fetch. In
 * a headless turn state holds nothing (only three property panels ever fill it),
 * so the two halves disagreed: the offer listed the workflow's own parameters and
 * every write to one of them was refused by the process-binding guard. The live
 * 2026-08-13 run shipped both task screens with `KUser_Id` empty exactly this
 * way — the value was in the inventory, the repair matched it by name, and the
 * write was rejected.
 *
 * The asymmetry is closed here rather than by making the write path async: the
 * caller that CAN fetch retries the synchronous write once with the workflow
 * supplied. Nothing is cached, so a workflow edited elsewhere cannot go stale,
 * and a write that still cannot resolve its source keeps failing — loudly.
 */

/** Marker the synchronous symbol builder puts on "this screen does not carry that value". */
const PROCESS_BINDING_GUARD_MARKER = "[Process-binding guard]";

/** One row of a multi-binding write result. */
interface IBindingResultRow {
  error?: unknown;
}

/** The shape both binding writers return (single write reports a bare `error`). */
interface IBindingWriteResult {
  error?: unknown;
  results?: IBindingResultRow[];
}

/** True when a write failed BECAUSE the process value could not be resolved. */
export function reportsMissingProcessSource(result: unknown): boolean {
  if (result === null || typeof result !== "object") return false;
  const record: IBindingWriteResult = result as IBindingWriteResult;
  const mentions = (value: unknown): boolean =>
    typeof value === "string" && value.includes(PROCESS_BINDING_GUARD_MARKER);
  if (mentions(record.error)) return true;
  if (!Array.isArray(record.results)) return false;
  return record.results.some((row: IBindingResultRow): boolean => row !== null && mentions(row?.error));
}

/** Everything {@link runProcessBindingWrite} needs from its caller. */
export interface IProcessBindingWriteInput {
  /** The synchronous write. Called a second time, unchanged, only after a load. */
  readonly runSync: () => unknown;
  /**
   * The process this screen belongs to — see {@link resolveProcessBindingWriteId}.
   *
   * Resolved LAZILY: a write that succeeded must not pay for, or be endangered
   * by, reading designer state it never needed.
   */
  readonly resolveProcessId: () => string | undefined;
  readonly fetchWorkflow: (processId: string) => Promise<IManagedBindingWorkflowLike | undefined>;
}

/**
 * Run a binding write, and if it was refused for a process value this screen
 * could not resolve, load the process and run it once more.
 *
 * The retry replays the WHOLE write. Re-applying a binding that already landed
 * writes the identical symbol, so the repeat is idempotent — and a partial batch
 * completing is the point.
 */
export async function runProcessBindingWrite(input: IProcessBindingWriteInput): Promise<unknown> {
  const first: unknown = input.runSync();
  if (!reportsMissingProcessSource(first)) return first;

  const processId: string = (input.resolveProcessId() ?? "").trim();
  if (processId.length === 0) return first;

  const workflow: IManagedBindingWorkflowLike | undefined = await input.fetchWorkflow(processId);
  // Fail closed: a refusal the load could not explain stays a refusal. Returning
  // the first result keeps the guard's own message, which names what the screen
  // does carry.
  if (workflow === undefined) return first;

  return withLoadedProcessWorkflow(workflow, input.runSync);
}

/** A tool input that may name the process its screen belongs to. */
export interface IProcessBindingWriteScope {
  processId?: unknown;
}

/**
 * Which process to load for this write.
 *
 * The caller's own answer wins, for the same reason it does when the inventory is
 * read: whoever created the screen is the one party that cannot be wrong about
 * which process it was generated for, while the browser's screen→step registry
 * can still be catching up on a screen created moments ago. The registry is the
 * fallback, and an ordinary screen yields nothing to load.
 */
export function resolveProcessBindingWriteId(
  sd: IManagedBindingSdStateLike,
  input: IProcessBindingWriteScope
): string | undefined {
  const named: string = typeof input?.processId === "string" ? input.processId.trim() : "";
  if (named.length > 0) return named;

  const link: IManagedTaskScreenLink | null = findManagedTaskScreenLink(sd?.project, sd?.sdId);
  const linked: string = link?.processId ?? "";
  return linked.length > 0 ? linked : undefined;
}
